import React, { useState, useRef, useEffect } from 'react';
import AccessibilityVisuallyHidden from '../AccessibilityVisuallyHidden';

// Define the SelectActions object for managing actions.
const SelectActions = {
    Close: 0,
    CloseSelect: 1,
    First: 2,
    Last: 3,
    Next: 4,
    Open: 5,
    PageDown: 6,
    PageUp: 7,
    Previous: 8,
    Select: 9,
    Type: 10,
};

// Utility functions
const filterOptions = (options = [], filter, exclude = []) => {
    return options.filter((option) => {
        const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
        return matches && exclude.indexOf(option) < 0;
    });
};

const getActionFromKey = (event, menuOpen) => {
    const { key, altKey, ctrlKey, metaKey } = event;
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' '];
    if (!menuOpen && openKeys.includes(key)) {
        return SelectActions.Open;
    }
    if (key === 'Home') return SelectActions.First;
    if (key === 'End') return SelectActions.Last;
    if (key === 'Backspace' || key === 'Clear' || (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)) {
        return SelectActions.Type;
    }
    if (menuOpen) {
        if (key === 'ArrowUp' && altKey) return SelectActions.CloseSelect;
        if (key === 'ArrowDown' && !altKey) return SelectActions.Next;
        if (key === 'ArrowUp') return SelectActions.Previous;
        if (key === 'PageUp') return SelectActions.PageUp;
        if (key === 'PageDown') return SelectActions.PageDown;
        if (key === 'Escape') return SelectActions.Close;
        if (key === 'Enter' || key === ' ') return SelectActions.CloseSelect;
    }
};

const getIndexByLetter = (options, filter, startIndex = 0) => {
    const orderedOptions = [...options.slice(startIndex), ...options.slice(0, startIndex)];
    const firstMatch = filterOptions(orderedOptions, filter)[0];
    const allSameLetter = (array) => array.every((letter) => letter === array[0]);

    if (firstMatch) return options.indexOf(firstMatch);
    if (allSameLetter(filter.split(''))) {
        const matches = filterOptions(orderedOptions, filter[0]);
        return options.indexOf(matches[0]);
    }
    return -1;
};

const getUpdatedIndex = (currentIndex, maxIndex, action) => {
    const pageSize = 10;
    switch (action) {
        case SelectActions.First:
            return 0;
        case SelectActions.Last:
            return maxIndex;
        case SelectActions.Previous:
            return Math.max(0, currentIndex - 1);
        case SelectActions.Next:
            return Math.min(maxIndex, currentIndex + 1);
        case SelectActions.PageUp:
            return Math.max(0, currentIndex - pageSize);
        case SelectActions.PageDown:
            return Math.min(maxIndex, currentIndex + pageSize);
        default:
            return currentIndex;
    }
};

const isElementInView = (element) => {
    const bounding = element.getBoundingClientRect();
    return (
        bounding.top >= 0 &&
        bounding.left >= 0 &&
        bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
};

const isScrollable = (element) => element && element.clientHeight < element.scrollHeight;

const maintainScrollVisibility = (activeElement, scrollParent) => {
    const { offsetHeight, offsetTop } = activeElement;
    const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

    const isAbove = offsetTop < scrollTop;
    const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

    if (isAbove) {
        scrollParent.scrollTo(0, offsetTop);
    } else if (isBelow) {
        scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
    }
};


const AccessibleCombobox = ({ label, options, onSelect, className, renderOption, dropdownClass, optionClass, arrowIcon }) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [searchString, setSearchString] = useState('');
    const searchTimeoutRef = useRef(null);
    const comboRef = useRef(null);
    const listboxRef = useRef(null);

    const handleKeyDown = (event) => {
        const max = options.length - 1;
        const action = getActionFromKey(event, open);

        switch (action) {
            case SelectActions.First:
            case SelectActions.Last:
                setOpen(true);
                setActiveIndex(action === SelectActions.First ? 0 : max);
                break;
            case SelectActions.Next:
                event.preventDefault();
                if (activeIndex === max) {
                    setActiveIndex(-1); // Wrap to the first item
                } else {
                    setActiveIndex(getUpdatedIndex(activeIndex, max, action));
                }
                break;
            case SelectActions.Previous:
                event.preventDefault();
                if (activeIndex === 0) {
                    setActiveIndex(-1); // Move focus back to combobox container
                    comboRef.current.focus();
                } else {
                    setActiveIndex(getUpdatedIndex(activeIndex, max, action));
                }
                break;
            case SelectActions.PageUp:
            case SelectActions.PageDown:
                event.preventDefault();
                setActiveIndex(getUpdatedIndex(activeIndex, max, action));
                break;
            case SelectActions.CloseSelect:
                event.preventDefault();
                if (activeIndex >= 0) {
                    onSelect(options[activeIndex]);
                }
                else {
                    setOpen(false);
                }
                break;
            case SelectActions.Close:
                setOpen(false);
                break;
            case SelectActions.Type:
                handleTyping(event.key);
                break;
            case SelectActions.Open:
                setOpen(true);
                if (activeIndex === -1) {
                    setActiveIndex(0);
                } else {
                    setActiveIndex(getUpdatedIndex(activeIndex, max, action));
                }
                break;
            default:
                break;
        }

        // New: Close the list if Enter is pressed while focus is on the combobox container
        if (!open && event.key === 'Enter') {
            setOpen(!open);
        }
    };

    const handleTyping = (char) => {
        const searchStr = getSearchString(char);
        const searchIndex = getIndexByLetter(options, searchStr, activeIndex + 1);

        if (searchIndex >= 0) {
            setActiveIndex(searchIndex);
        } else {
            setSearchString('');
        }
    };

    const getSearchString = (char) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => setSearchString(''), 500);
        setSearchString((prev) => prev + char);
        return searchString + char;
    };

    useEffect(() => {
        if (open) {
            comboRef.current.focus();
        }
    }, [open]);

    return (
        <div>
            <AccessibilityVisuallyHidden> <label htmlFor="combo">{label}</label></AccessibilityVisuallyHidden>
            <div
                className={className}
                ref={comboRef}
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-controls="listbox"
                tabIndex={0}
                onKeyDown={handleKeyDown}
                onClick={() => setOpen(!open)}
            >
                <span>{label}</span>
                {arrowIcon}
            </div>
            {open && (
                <ul
                    ref={listboxRef}
                    role="listbox"
                    id="listbox"
                    aria-activedescendant={`option-${activeIndex}`}
                    tabIndex={-1}
                    className={dropdownClass}
                >
                    {options.map((option, index) => (
                        <li
                            id={`option-${index}`}
                            role="option"
                            aria-selected={activeIndex === index}
                            key={index}
                            className={optionClass}
                            onClick={() => onSelect(option)}
                        >
                            {renderOption(option, index)}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AccessibleCombobox;

